// src/oura/components/onboarding/drawers.tsx
//
// The drawers the staff onboarding screen opens. Each one carries its state
// at the top and one action pinned at the bottom.
//
//   ComposeDrawer   read Oura's draft, change it, send it
//   SentDrawer      what went out, to whom and when
//   ReleaseDrawer   the login and the access request, sent together
//   AccessDrawer    one access: what we hold, and confirming an invite
//   StartScanDrawer what the first AI check runs on, and the go
//   AddLoginDrawer  a login for another person at the client

import { useState, type ReactNode } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { ArrowLink, Drawer, DrawerBlock, InnerStat, PillButton, SURFACE, Tag, TYPE, type Tone } from "@oura/ui";
import { useSnackbar } from "@hook/use-snackbar";
import {
    ACCESS_REQUEST_TEMPLATE_ID, KICKOFF_TEMPLATE_ID, confirmAccess, releaseOnboarding, sendOnboardingMessage, startFirstScan,
    type AccessItem, type AccessKey, type AccessStatus, type MessageDraft, type MessageKind, type Onboarding, type OnboardingPerson, type SentMessage,
} from "@service/aeo/onboarding";
import { createClientUser } from "@service/aeo/clientReview";
import { updateOnboardingContact } from "@service/aeo/onboarding";
import { ACCESS_LABEL, FIRST_CHECK_ENGINES, FIRST_CHECK_TIME, callName, dayMonth, dayTime, isHeld, isOneSetup, lastSent, nameStop, onlineBeforeScan, type AccessRowKey } from "../../onboarding/derive";
import { MESSAGE_LABEL, draftAccessRequest, draftFor, draftKickoff, formatPeople, isEmail, loginRecipient } from "../../onboarding/drafts";
import { GBP_STEPS, GOOGLE_STEPS, SITE_GUIDES } from "../../onboarding/guides";
import { clearFixed, inputClass, readMessage, readRecipients, sameMessage, textareaClass, toMessageText, toRecipientText, type MessageErrors, type MessageText } from "../../onboarding/messageText";
import { GuideSteps } from "../../onboarding/StepText";
import { Field, MessageFields, Refusal, SenderLine } from "./fields";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";

/** A drawer stays open while its request is on its way: closing it would not stop the send. */
const stay = () => {};

/**
 * What staff typed into a draft, kept while its drawer is closed. Closing a
 * drawer by accident never throws an edit away; sending it does.
 */
const drafts = new Map<string, MessageText>();
/** When a request throws instead of answering: the controls come back and say so. */
const NOT_SENT = "The email was not sent. Check your connection and try again.";
const NOT_SAVED = "Nothing changed. Check your connection and try again.";

const names = (people: OnboardingPerson[]) => people.map((p) => callName(p.name)).join(", ");

/* ------------------------------------------------------------------------- *
 * Compose
 * ------------------------------------------------------------------------- */

interface ComposeProps {
    open: boolean;
    onboarding: Onboarding;
    onClose: () => void;
    onSent: (next: Onboarding) => void;
}

/**
 * The kick-off and the access request are templates with nothing to edit (who
 * they go to is the only choice); every other message is a draft staff change.
 */
export function ComposeDrawer({ kind, ...rest }: ComposeProps & { kind: MessageKind }) {
    return kind === "kickoff" || kind === "access_request"
        // Keyed by business and kind: the kick-off and the access request are the
        // same component, which sets its recipients up once. Without a key, a
        // drawer that stayed mounted while the kind changed would send one
        // email to the other's recipients.
        ? <TemplateComposeDrawer key={`${rest.onboarding.businessId}:${kind}`} kind={kind} {...rest} />
        : <EditableComposeDrawer kind={kind} {...rest} />;
}

/**
 * A template email (the kick-off, the access request): who it goes to and who is
 * copied, and no subject or body, which are not read or sent (#4155, #4261).
 */
function TemplateComposeDrawer({ open, kind, onboarding, onClose, onSent }: ComposeProps & { kind: "kickoff" | "access_request" }) {
    const snackbar = useSnackbar();
    const draftKey = `${onboarding.businessId}:${kind}`;
    const [draft] = useState<MessageText>(() => toRecipientText(kind === "kickoff" ? draftKickoff({ contact: onboarding.contact }) : draftAccessRequest(onboarding)));
    const [text, setText] = useState<MessageText>(() => drafts.get(draftKey) ?? draft);
    const [errors, setErrors] = useState<MessageErrors | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    const [sending, setSending] = useState(false);

    const change = (next: MessageText) => {
        setErrors((was) => clearFixed(was, text, next));
        setRefused(null);
        setText(next);
        drafts.set(draftKey, next);
    };
    const send = async () => {
        const read = readRecipients(text);
        setErrors(read.errors);
        setRefused(null);
        if (!read.draft) return;
        setSending(true);
        try {
            const result = await sendOnboardingMessage(onboarding.businessId, kind, read.draft);
            // Refused (502 `send_failed` included): said here, and the step stays unsent.
            if (!result.ok) { setRefused(result.message); return; }
            drafts.delete(draftKey);
            snackbar.showSuccess(`Sent to ${names(read.draft.to)}`);
            onSent(result.onboarding);
            onClose();
        } catch {
            setRefused(NOT_SENT);
        } finally {
            setSending(false);
        }
    };

    return (
        <Drawer
            open={open}
            onClose={sending ? stay : onClose}
            title={kind === "kickoff" && isOneSetup(onboarding) ? "Setup email" : MESSAGE_LABEL[kind]}
            sub={`${nameStop(onboarding.businessName)} The email is a template. Choose who it goes to.`}
            width={560}
            foot={
                <>
                    {refused && <Refusal className="basis-full">{refused}</Refusal>}
                    <PillButton variant="primary" className="max-sm:h-9" busy={sending} writes onClick={() => void send()}>Send from Oura</PillButton>
                    {!sameMessage(text, draft) && (
                        <PillButton variant="text" className="ml-auto max-sm:h-9" onClick={() => { setText(draft); setErrors(null); drafts.delete(draftKey); }}>
                            {kind === "kickoff" ? "Back to the contact" : "Back to who the form named"}
                        </PillButton>
                    )}
                </>
            }
        >
            <div className={SURFACE.block}>
                <MessageFields value={text} onChange={change} errors={errors} disabled={sending} recipientsOnly />
            </div>
            <SenderLine />
        </Drawer>
    );
}

function EditableComposeDrawer({
    open,
    kind,
    onboarding,
    onClose,
    onSent,
}: ComposeProps & { kind: Exclude<MessageKind, "kickoff" | "access_request"> }) {
    const snackbar = useSnackbar();
    const draftKey = `${onboarding.businessId}:${kind}`;
    const [draft] = useState<MessageText>(() => toMessageText(draftFor(kind, onboarding)));
    const [text, setText] = useState<MessageText>(() => drafts.get(draftKey) ?? draft);
    const [errors, setErrors] = useState<MessageErrors | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(false);

    const change = (next: MessageText) => {
        setErrors((was) => clearFixed(was, text, next));
        setRefused(null);
        setText(next);
        drafts.set(draftKey, next);
    };
    const send = async () => {
        const read = readMessage(text);
        setErrors(read.errors);
        setRefused(null);
        if (!read.draft) return;
        setSending(true);
        try {
            const result = await sendOnboardingMessage(onboarding.businessId, kind, read.draft);
            if (!result.ok) { setRefused(result.message); return; }
            drafts.delete(draftKey);
            snackbar.showSuccess(`Sent to ${names(read.draft.to)}`);
            onSent(result.onboarding);
            onClose();
        } catch {
            setRefused(NOT_SENT);
        } finally {
            setSending(false);
        }
    };
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`${text.subject}\n\n${text.body}`);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
        } catch {
            setRefused("Could not copy. Select the text and copy it by hand.");
        }
    };

    return (
        <Drawer
            open={open}
            onClose={sending ? stay : onClose}
            title={MESSAGE_LABEL[kind]}
            sub={`${nameStop(onboarding.businessName)} Oura's draft. Change anything before it goes.`}
            width={560}
            foot={
                <>
                    {refused && <Refusal className="basis-full">{refused}</Refusal>}
                    <PillButton variant="primary" className="max-sm:h-9" busy={sending} writes onClick={() => void send()}>Send from Oura</PillButton>
                    <PillButton className="max-sm:h-9" icon={copied ? CheckIcon : CopyIcon} onClick={() => void copy()}>{copied ? "Copied" : "Copy"}</PillButton>
                    {!sameMessage(text, draft) && (
                        <PillButton variant="text" className="ml-auto max-sm:h-9" onClick={() => { setText(draft); setErrors(null); drafts.delete(draftKey); }}>
                            Back to Oura's draft
                        </PillButton>
                    )}
                </>
            }
        >
            <div className={SURFACE.block}>
                <MessageFields value={text} onChange={change} errors={errors} disabled={sending} />
            </div>
            <SenderLine />
        </Drawer>
    );
}

/* ------------------------------------------------------------------------- *
 * Sent
 * ------------------------------------------------------------------------- */

export function SentDrawer({ open, message, businessName, status, sentTo, formLink, onSendAgain, onResend, resendSameEmail, failure, onClose }: {
    open: boolean;
    message: SentMessage;
    businessName: string;
    /** 1 line on where things stand since it went, such as whether a login was used. */
    status?: string;
    /** The contact's address NOW, to say where sending it again would go. */
    sentTo?: string;
    /** The form link, shown for a kick-off that went out as a template. */
    formLink?: string | null;
    /**
     * Offer to send this kind again (#3992). Absent: no offer. It drafts a new
     * one rather than resending these words, because the reason to send again
     * is usually that the address was wrong — and the draft reads the contact
     * as it stands now.
     */
    onSendAgain?: () => void;
    /**
     * Send again by asking the server, with no draft in between: the login
     * email is a template and a resend mints a new link, so there is nothing
     * for a person to edit (#4025 FE-3). Mutually exclusive with
     * `onSendAgain`, which opens a composer instead.
     *
     * Returns the server's refusal sentence, or null when it went.
     */
    onResend?: () => Promise<string | null>;
    /**
     * With `onResend`: the resend sends THESE words again to the people on this
     * message (an access request), rather than minting a new login link. The note
     * above the button then says so, instead of the login's "the link in this
     * email stops working", which would be false here.
     */
    resendSameEmail?: boolean;
    /** Shown above the message when the last send FAILED: the server's own sentence. */
    failure?: string | null;
    onClose: () => void;
}) {
    const [resending, setResending] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    const resend = async () => {
        if (!onResend) return;
        setResending(true);
        setRefused(null);
        const message = await onResend();
        setResending(false);
        setRefused(message);
        // It went: the snackbar says so, and the sheet closes (Angel, 15 Oct). A refusal keeps it open with the reason.
        if (message === null) onClose();
    };
    // Where it WENT, against where it would go now: a corrected contact is the
    // reason this action exists, so the difference is said rather than implied.
    const wentTo = message.to[0]?.email?.trim() ?? null;
    const goesElsewhere = Boolean(onSendAgain && sentTo && wentTo && sentTo.toLowerCase() !== wentTo.toLowerCase());
    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={MESSAGE_LABEL[message.kind]}
            sub={`${nameStop(businessName)} Sent ${dayTime(message.sentAt)} by ${message.sentBy}.`}
            width={560}
            foot={
                onSendAgain || onResend ? (
                    <>
                        {refused && <Refusal className="basis-full">{refused}</Refusal>}
                        <PillButton
                            variant="primary"
                            className="max-sm:h-9"
                            busy={resending}
                            writes
                            onClick={onSendAgain ?? (() => void resend())}
                        >
                            Send again
                        </PillButton>
                        <PillButton className="max-sm:h-9" disabled={resending} onClick={onClose}>Close</PillButton>
                    </>
                ) : undefined
            }
        >
            {/* The send FAILED, in the server's words. Before BE-1 a failed
                send still read as "sent", so the screen told staff to wait for
                a client who had been told nothing (#4025 FE-3). */}
            {failure && <Refusal>{failure}</Refusal>}
            {goesElsewhere && (
                <DrawerBlock label="The contact has changed">
                    This went to {wentTo}. Sending it again goes to {sentTo}.
                </DrawerBlock>
            )}
            {/* Said before it is pressed, not after: each resend stops the
                earlier link working, so a client holding the old email finds a
                dead one. */}
            {onResend && (
                <DrawerBlock label="Sending it again">
                    {resendSameEmail ? (
                        // The access request is a template: it goes again to the same
                        // people, in the template's words, with no link to replace.
                        // An earlier message that carried its own words is not
                        // repeated word for word (#4261).
                        <>
                            The access request goes again to {formatPeople(message.to)}
                            {message.cc.length > 0 ? `, with a copy to ${formatPeople(message.cc)}` : ""}. It is the template email.
                        </>
                    ) : (
                        <>A new link goes to {sentTo ?? "the contact"}, and the link in this email stops working.</>
                    )}
                </DrawerBlock>
            )}
            {status && <DrawerBlock label="Since then">{status}</DrawerBlock>}
            <div className={cn(SURFACE.block, "space-y-4")}>
                <Line label="To" value={formatPeople(message.to)} />
                <Line label="Copy to" value={message.cc.length > 0 ? formatPeople(message.cc) : "Nobody"} />
                {(message.kind === "kickoff" || message.kind === "access_request") && !message.body ? (
                    // A template has no free text to show: the template and the
                    // value it was filled with are what went out (#4155, #4261).
                    // An access request sent before it was a template keeps its words.
                    <>
                        <Line label="Template" value={<>{MESSAGE_LABEL[message.kind]} <span className="text-gray-500">({message.kind === "kickoff" ? KICKOFF_TEMPLATE_ID : ACCESS_REQUEST_TEMPLATE_ID})</span></>} />
                        <Line label="First name" value={callName(message.to[0]?.name ?? "")} />
                        {message.kind === "kickoff" && <Line label="Form link" value={<span className="break-all">{formLink ?? "Not on record"}</span>} />}
                    </>
                ) : (
                    <>
                        <Line label="Subject" value={message.subject ?? ""} />
                        <Line label="Message" value={<span className="whitespace-pre-wrap">{message.body ?? ""}</span>} />
                    </>
                )}
            </div>
        </Drawer>
    );
}

/** A read-only field: the same label and spacing as its editable twin. */
function Line({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div>
            <p className={cn(TYPE.lab, "m-0 mb-1.5")}>{label}</p>
            <div className={cn(TYPE.tb, "break-words text-gray-900")}>{value}</div>
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Release: the login and the access request
 * ------------------------------------------------------------------------- */

/** "Send" to the eye, and the full name of what it sends to a screen reader. */
function Include({ checked, onChange, name }: { checked: boolean; onChange: (v: boolean) => void; name: string }) {
    return (
        <label className={cn(TYPE.tc, "flex min-h-9 cursor-pointer items-center gap-2 font-medium text-gray-900")}>
            <input type="checkbox" aria-label={name} checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-blue-600" />
            <span aria-hidden>Send</span>
        </label>
    );
}

/**
 * The login email as it will be sent: read-only, and labelled as such.
 *
 * Staff could read the whole access-request message before sending it and only
 * a one-line summary of the login (#4025 FE-4). This shows the email.
 *
 * Deliberately NOT a set of inputs. The body is a SendGrid template on the
 * server and the link is always the literal `[sign-in link]`, because no
 * token ever reaches the payload -- so an editable box would offer control
 * that does not exist, and a staffer who edited it would watch their edit
 * vanish.
 */
/**
 * The login email as the server renders it, with ONE thing taken from the
 * screen: who it goes to.
 *
 * `to` is passed in rather than read off `draft`, because the address is
 * editable above and the server's copy is whatever it was when the payload was
 * read. Showing `draft.to` here let the field say one address while the
 * preview said another, and the field is the one that gets sent (#4049).
 */
function LoginPreview({ draft, to }: { draft: MessageDraft & { editable: false }; to: OnboardingPerson[] }) {
    const people = (list: OnboardingPerson[]) => list.map((p) => (p.name ? `${p.name} <${p.email}>` : p.email)).join(", ");
    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className={cn(TYPE.lab, "m-0")}>The email they get</p>
                <Tag tone="gray">Not editable</Tag>
            </div>
            <div className={cn(TYPE.tc, "space-y-1 rounded-xl bg-white px-3 py-2.5 ring-1 ring-inset ring-gray-200")}>
                {/* `to` is [] when the field above is empty, which is a real
                    state and not an empty line. */}
                <p className="m-0 break-all text-gray-500">
                    <span className="text-gray-400">To </span>
                    {to.length > 0 ? people(to) : <span className="text-amber-700">Add a login email above.</span>}
                </p>
                {draft.cc.length > 0 && (
                    <p className="m-0 break-all text-gray-500"><span className="text-gray-400">Cc </span>{people(draft.cc)}</p>
                )}
                <p className="m-0 font-medium text-gray-900">{draft.subject}</p>
                {/* The server's own text, as written, newlines kept. */}
                <p className={cn(TYPE.tb, "m-0 whitespace-pre-wrap text-gray-700")}>{draft.body}</p>
            </div>
            <p className={cn(TYPE.tm, "m-0 text-gray-400")}>
                The real email is a template, so the layout differs. The sign-in link is put in when it is sent.
            </p>
        </div>
    );
}

export function ReleaseDrawer({
    open,
    onboarding,
    onClose,
    onSent,
}: {
    open: boolean;
    onboarding: Onboarding;
    onClose: () => void;
    onSent: (next: Onboarding) => void;
}) {
    const snackbar = useSnackbar();
    const loginOwed = onboarding.login.status === "not_sent";
    const requestOwed = !lastSent(onboarding, "access_request");
    const [withLogin, setWithLogin] = useState(loginOwed);
    const [withRequest, setWithRequest] = useState(requestOwed);
    // Who the login is for, and what to say about the address: loginRecipient()
    // in drafts.ts. The form's answers name whoever filled the form in, not the
    // client, so they are not the default (#4049).
    const recipient = loginRecipient(onboarding);
    const [name, setName] = useState(recipient.name);
    const [email, setEmail] = useState(recipient.email);
    // The access request is a template: who it goes to is the only thing staff
    // choose, and there is no wording to edit or to promise a login in (#4261).
    const draftKey = `${onboarding.businessId}:release`;
    const [text, setText] = useState<MessageText>(() => drafts.get(draftKey) ?? toRecipientText(draftAccessRequest(onboarding)));
    const [loginErrors, setLoginErrors] = useState<{ name?: string; email?: string }>({});
    const [errors, setErrors] = useState<MessageErrors | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    const [sending, setSending] = useState(false);

    const sendLogin = loginOwed && withLogin;
    const sendRequest = requestOwed && withRequest;
    const cta = sendLogin && sendRequest ? "Send both" : sendLogin ? "Send the login" : "Send the access request";
    const toggleLogin = (next: boolean) => {
        setWithLogin(next);
        setRefused(null);
    };
    const change = (next: MessageText) => {
        setErrors((was) => clearFixed(was, text, next));
        setRefused(null);
        setText(next);
        drafts.set(draftKey, next);
    };

    const send = async () => {
        setRefused(null);
        const nextLoginErrors: { name?: string; email?: string } = {};
        if (sendLogin && !name.trim()) nextLoginErrors.name = "Add a name.";
        if (sendLogin && !email.trim()) nextLoginErrors.email = "Add an email.";
        else if (sendLogin && !isEmail(email)) nextLoginErrors.email = "Check this address.";
        setLoginErrors(nextLoginErrors);
        const read = sendRequest ? readRecipients(text) : null;
        setErrors(read?.errors ?? null);
        if (Object.keys(nextLoginErrors).length > 0 || (read && !read.draft)) return;

        setSending(true);
        try {
            const result = await releaseOnboarding(onboarding.businessId, {
                login: sendLogin ? { name: name.trim(), email: email.trim() } : null,
                accessRequest: read?.draft ?? null,
            });
            if (!result.ok) { setRefused(result.message); return; }
            const sentTo = [sendLogin ? callName(name) : null, read?.draft ? callName(read.draft.to[0].name) : null].filter(Boolean);
            drafts.delete(draftKey);
            snackbar.showSuccess(sendLogin && read?.draft ? "Both sent" : "Sent", `To ${Array.from(new Set(sentTo)).join(" and ")}.`);
            onSent(result.onboarding);
            onClose();
        } catch {
            setRefused(NOT_SENT);
        } finally {
            setSending(false);
        }
    };

    return (
        <Drawer
            open={open}
            onClose={sending ? stay : onClose}
            title={loginOwed && requestOwed ? "Login and access request" : loginOwed ? "Login" : "Access request"}
            sub={`${nameStop(onboarding.businessName)} Oura's draft. Change anything before it goes.`}
            width={560}
            foot={
                <>
                    {refused && <Refusal className="basis-full">{refused}</Refusal>}
                    <PillButton variant="primary" className="max-sm:h-9" busy={sending} disabled={!sendLogin && !sendRequest} writes onClick={() => void send()}>{cta}</PillButton>
                    <PillButton className="max-sm:h-9" onClick={onClose}>Not now</PillButton>
                </>
            }
        >
            {loginOwed && (
                <div className={cn(SURFACE.block, "space-y-4")}>
                    <div className="flex items-center justify-between gap-3">
                        <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>Login</h3>
                        {requestOwed && <Include checked={withLogin} onChange={toggleLogin} name="Send the login" />}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="Name" error={loginErrors.name}>
                            {({ id, describedBy }) => (
                                <input
                                    id={id}
                                    aria-describedby={describedBy}
                                    aria-invalid={!!loginErrors.name}
                                    className={inputClass(loginErrors.name)}
                                    value={name}
                                    onChange={(e) => { setName(e.target.value); setLoginErrors((was) => ({ ...was, name: undefined })); setRefused(null); }}
                                    disabled={sending || !withLogin}
                                />
                            )}
                        </Field>
                        <Field label="Login email" error={loginErrors.email}>
                            {({ id, describedBy }) => (
                                <input
                                    id={id}
                                    type="email"
                                    aria-describedby={describedBy}
                                    aria-invalid={!!loginErrors.email}
                                    className={inputClass(loginErrors.email)}
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); setLoginErrors((was) => ({ ...was, email: undefined })); setRefused(null); }}
                                    disabled={sending || !withLogin}
                                    autoComplete="off"
                                />
                            )}
                        </Field>
                    </div>
                    <p
                        role={recipient.warn ? "status" : undefined}
                        className={cn(TYPE.tc, "m-0", recipient.warn ? "font-medium text-amber-700" : "text-gray-500")}
                    >
                        {recipient.note}
                    </p>
                    {/* THE EMAIL ITSELF, read-only (#4025 FE-4). The Access
                        request block below shows its whole message and this one
                        showed a one-line summary of what the client "gets", so
                        staff could not check the login email before sending the
                        thing they were about to send.

                        Not editable, and said so: the body is a SendGrid
                        template on the server, and the link in it is always the
                        literal `[sign-in link]` because no token ever reaches
                        the payload. An editable box would promise control that
                        does not exist.

                        `login.draft` is the server's own rendering (BE-2). With
                        it absent -- a payload stored before that change -- the
                        old summary stands rather than a body invented here. */}
                    {onboarding.login.draft ? (
                        <LoginPreview
                            draft={onboarding.login.draft}
                            to={email.trim() ? [{ name: name.trim(), email: email.trim() }] : []}
                        />
                    ) : (
                        <InnerStat
                            label="What they get"
                            // An Online client's login goes before any check (JOURNEY-SPEC §2.4a): it promises only the plan.
                            value={onlineBeforeScan(onboarding)
                                ? "Your Oura login is ready. Set your password to see your plan. Your first AI check runs when brand checks go live."
                                : "Your Oura login is ready. Set your password to see your first AI check and your plan."}
                            sub="With a set-password link. It lasts 7 days."
                        />
                    )}
                </div>
            )}

            {requestOwed && (
                <div className={cn(SURFACE.block, "space-y-4")}>
                    <div className="flex items-center justify-between gap-3">
                        <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>Access request</h3>
                        {loginOwed && <Include checked={withRequest} onChange={(v) => { setWithRequest(v); setRefused(null); }} name="Send the access request" />}
                    </div>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                        The email is a template. Choose who it goes to.
                        {!onboarding.accessContact?.email && ` The form gave no email for access, so this goes to ${callName(onboarding.contact.name)}.`}
                    </p>
                    <MessageFields value={text} onChange={change} errors={errors} disabled={sending || !withRequest} recipientsOnly />
                    <SenderLine />
                </div>
            )}
        </Drawer>
    );
}

/* ------------------------------------------------------------------------- *
 * Access
 * ------------------------------------------------------------------------- */

// The brand's status words (audit, 24 Oct 2026): four statuses had four words
// of their own. What is finer (who told us when, held since) is the line under it.
const ACCESS_TAG: Record<AccessStatus, { label: string; tone: Tone }> = {
    not_started: { label: "Not started", tone: "gray" },
    claimed: { label: "Needs us", tone: "blue" },
    confirmed: { label: "Done", tone: "green" },
    connected: { label: "Done", tone: "green" },
};

export function AccessTag({ status }: { status: AccessStatus }) {
    const { label, tone } = ACCESS_TAG[status];
    return <Tag tone={tone}>{label}</Tag>;
}

function AccessFacts({ item }: { item: AccessItem }) {
    const pair = isHeld(item) || item.status === "claimed";
    return (
        <div className={cn("grid grid-cols-1 gap-3", pair && "sm:grid-cols-2")}>
            <InnerStat label="Role" value={item.role} />
            {isHeld(item) && <InnerStat label="Held since" value={dayMonth(item.since)} sub={item.detail ?? undefined} />}
            {item.status === "claimed" && (
                <InnerStat label="Told us" value={`${callName(item.claimedBy ?? "The client")}, ${dayMonth(item.claimedAt)}`} />
            )}
        </div>
    );
}

export function AccessDrawer({
    open,
    row,
    onboarding,
    connectorsHref,
    onClose,
    onChanged,
}: {
    open: boolean;
    row: AccessRowKey;
    onboarding: Onboarding;
    connectorsHref: string;
    onClose: () => void;
    onChanged: (next: Onboarding) => void;
}) {
    const snackbar = useSnackbar();
    const [busy, setBusy] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    // Confirming counts the access as held, so it asks first (#4544): the client saying so is not the invite arriving.
    const [askingConfirm, setAskingConfirm] = useState(false);

    const keys: AccessKey[] = row === "google" ? ["gsc", "ga4"] : [row];
    const items = keys.map((k) => onboarding.access.find((a) => a.key === k)).filter((a): a is AccessItem => !!a);
    const guide = SITE_GUIDES[onboarding.siteBuilder ?? "other"];
    // An invite is confirmed by a person. Google's approval is tested by Oura.
    const byInvite = row !== "google";
    const item = items[0];
    const title = row === "google" ? "Search Console and Analytics" : ACCESS_LABEL[row];

    const confirm = async (confirmed: boolean, quiet = false) => {
        if (!item) return;
        setBusy(true);
        setRefused(null);
        let result: Awaited<ReturnType<typeof confirmAccess>>;
        try {
            result = await confirmAccess(onboarding.businessId, item.key, confirmed);
        } catch {
            result = { ok: false, code: null, message: NOT_SAVED };
        } finally {
            setBusy(false);
        }
        if (!result.ok) {
            if (quiet) snackbar.showError("Not undone", result.message);
            else setRefused(result.message);
            return;
        }
        onChanged(result.onboarding);
        if (confirmed) {
            // The drawer closes on a confirmation, so the way back rides on the toast.
            snackbar.showSuccess(`${title} confirmed`, undefined, undefined, { actionLabel: "Undo", onAction: () => void confirm(false, true) });
            onClose();
        }
    };

    // An invite can only be in the inbox once the client was asked for it, or says it is sent. One setup asks in the Setup email.
    const asked = !!lastSent(onboarding, isOneSetup(onboarding) ? "kickoff" : "access_request") || item?.status === "claimed";
    let foot: ReactNode = null;
    if (byInvite && item) {
        foot = item.status === "confirmed"
            ? <PillButton className="max-sm:h-9" busy={busy} writes onClick={() => void confirm(false)}>Undo the confirmation</PillButton>
            : (
                <>
                    <PillButton variant="primary" className="max-sm:h-9" busy={busy} disabled={!asked} writes onClick={() => setAskingConfirm(true)}>Confirm the invite</PillButton>
                    <span className={cn(TYPE.tc, "text-gray-500")}>{asked ? "Check the oura@ inbox first." : isOneSetup(onboarding) ? "Send the Setup email first." : "Send the access request first."}</span>
                </>
            );
    }

    // Mounted beside the drawer, so its focus and Esc are its own.
    const confirmDialog = byInvite && item ? (
        <Dialog open={askingConfirm} onOpenChange={(o) => { if (!o && !busy) setAskingConfirm(false); }}>
            <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl">
                <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">Confirm the {title} invite?</DialogTitle>
                <DialogDescription asChild>
                    <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                        <p className="m-0">
                            Only confirm once the invite from {onboarding.businessName} is in the oura@ourteam.ai inbox and accepted{item.role ? <>, as <span className="font-medium text-gray-900">{item.role}</span></> : null}.
                        </p>
                        <p className="m-0 text-gray-500">Oura then counts {title} as held. You can undo it.</p>
                    </div>
                </DialogDescription>
                <div className="mt-5 flex justify-end gap-2">
                    <PillButton size="sm" disabled={busy} onClick={() => setAskingConfirm(false)}>Cancel</PillButton>
                    <PillButton size="sm" variant="primary" busy={busy} writes onClick={() => { setAskingConfirm(false); void confirm(true); }}>Confirm the invite</PillButton>
                </div>
            </DialogContent>
        </Dialog>
    ) : null;

    return (
        <>
        <Drawer
            open={open}
            onClose={busy ? stay : onClose}
            title={title}
            sub={
                <span className="flex flex-wrap items-center gap-2">
                    {/* 1 item: its status up here. 2 items: each block below says its own. */}
                    {items.length === 1 && <AccessTag status={items[0].status} />}
                    <span>{onboarding.businessName}</span>
                </span>
            }
            width={560}
            foot={foot || refused ? <>{refused && <Refusal className="basis-full">{refused}</Refusal>}{foot}</> : undefined}
        >
            {row === "google" ? (
                items.map((a) => (
                    <div key={a.key} className={cn(SURFACE.block, "space-y-3")}>
                        <div className="flex items-center justify-between gap-3">
                            <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{ACCESS_LABEL[a.key]}</h3>
                            <AccessTag status={a.status} />
                        </div>
                        <AccessFacts item={a} />
                    </div>
                ))
            ) : item && (
                <div className={SURFACE.block}><AccessFacts item={item} /></div>
            )}

            <DrawerBlock label="How it arrives">
                {byInvite
                    ? "By invite. The client adds us, and we confirm it here."
                    : "The client presses Connect Google on their Set-up page. Oura tests it."}
                {row !== "gbp" && (
                    <div className="mt-2">
                        <ArrowLink to={connectorsHref}>{row === "website" ? "Connect Oura to the site" : "Open Connectors"}</ArrowLink>
                    </div>
                )}
            </DrawerBlock>

            <div className={SURFACE.block}>
                <p className={cn(TYPE.lab, "m-0 mb-1")}>
                    What the client sees{row === "website" ? `, for ${guide.label}` : ""}
                </p>
                <GuideSteps steps={row === "gbp" ? GBP_STEPS : row === "website" ? guide.steps : GOOGLE_STEPS} />
            </div>
        </Drawer>
        {confirmDialog}
        </>
    );
}

/* ------------------------------------------------------------------------- *
 * The first AI check: staff read what it will run on, then start it
 * ------------------------------------------------------------------------- */

/** "Bangsar, Mont Kiara" or 1 per line, into a clean list. */
function splitList(text: string): string[] {
    return text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
}

export function StartScanDrawer({
    open,
    onboarding,
    onClose,
    onStarted,
}: {
    open: boolean;
    onboarding: Onboarding;
    onClose: () => void;
    onStarted: (next: Onboarding) => void;
}) {
    const snackbar = useSnackbar();
    const answers = onboarding.form.answers;
    const failed = onboarding.scan.status === "failed";
    // The client's answer wins: they confirmed the areas on the form.
    const [services, setServices] = useState((answers?.services ?? []).join(", "));
    const [areas, setAreas] = useState((answers?.outletAreas.length ? answers.outletAreas : onboarding.outletAreas).join(", "));
    const [errors, setErrors] = useState<{ services?: string; areas?: string }>({});
    const [refused, setRefused] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const start = async () => {
        const inputs = { services: splitList(services), outletAreas: splitList(areas) };
        const found: { services?: string; areas?: string } = {};
        if (inputs.services.length === 0) found.services = "Add at least 1 service.";
        if (inputs.outletAreas.length === 0) found.areas = "Add at least 1 area.";
        setErrors(found);
        setRefused(null);
        if (found.services || found.areas) return;
        setBusy(true);
        try {
            const result = await startFirstScan(onboarding.businessId, inputs);
            if (!result.ok) { setRefused(result.message); return; }
            snackbar.showSuccess("The first AI check has started", FIRST_CHECK_TIME);
            onStarted(result.onboarding);
            onClose();
        } catch {
            setRefused("The AI check did not start. Check your connection and try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Drawer
            open={open}
            onClose={busy ? stay : onClose}
            title={failed ? "Run the first AI check again" : "Start the first AI check"}
            sub={`${nameStop(onboarding.businessName)} ${answers ? `Answered ${dayTime(onboarding.form.answeredAt)}.` : "No answers yet."}`}
            width={560}
            foot={
                <>
                    {refused && <Refusal className="basis-full">{refused}</Refusal>}
                    <PillButton variant="primary" className="max-sm:h-9" busy={busy} writes onClick={() => void start()}>
                        {failed ? "Run it again" : "Start the AI check"}
                    </PillButton>
                    <PillButton className="max-sm:h-9" onClick={onClose}>Not now</PillButton>
                </>
            }
        >
            {failed && onboarding.scan.failure && <Refusal>{onboarding.scan.failure}</Refusal>}
            <div className={cn(SURFACE.block, "space-y-4")}>
                <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>What it runs on</h3>
                <Field label="Services" error={errors.services} hint={answers ? "From the form. 3 to 5 is best." : undefined}>
                    {({ id, describedBy }) => (
                        <textarea
                            id={id}
                            aria-describedby={describedBy}
                            aria-invalid={!!errors.services}
                            rows={3}
                            className={textareaClass(errors.services)}
                            value={services}
                            onChange={(e) => { setServices(e.target.value); setErrors((was) => ({ ...was, services: undefined })); }}
                        />
                    )}
                </Field>
                <Field label="Outlet areas" error={errors.areas} hint={answers?.outletAreas.length ? "Confirmed by the client on the form." : "From signing. The client has not confirmed them."}>
                    {({ id, describedBy }) => (
                        <input
                            id={id}
                            aria-describedby={describedBy}
                            aria-invalid={!!errors.areas}
                            className={inputClass(errors.areas)}
                            value={areas}
                            onChange={(e) => { setAreas(e.target.value); setErrors((was) => ({ ...was, areas: undefined })); }}
                        />
                    )}
                </Field>
            </div>
            <DrawerBlock label="What happens">
                {/* The engines come from the one list the Visibility screen reports
                    (VIS_ENGINES + ENGINE_LABEL), so a fourth engine cannot appear
                    there while this sentence still names two. The time is measured;
                    see FIRST_CHECK_MINUTES. */}
                50 questions customers ask, on {FIRST_CHECK_ENGINES}. {FIRST_CHECK_TIME} It becomes the baseline every later check is compared with.
            </DrawerBlock>
        </Drawer>
    );
}

/* ------------------------------------------------------------------------- *
 * Another login, for a second person at the client
 * ------------------------------------------------------------------------- */

export function AddLoginDrawer({ open, businessId, businessName, onClose }: { open: boolean; businessId: string; businessName: string; onClose: () => void }) {
    const snackbar = useSnackbar();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
    const [refused, setRefused] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const send = async () => {
        const found: { name?: string; email?: string } = {};
        if (!name.trim()) found.name = "Add a name.";
        if (!email.trim()) found.email = "Add an email.";
        else if (!isEmail(email)) found.email = "Check this address.";
        setErrors(found);
        setRefused(null);
        if (found.name || found.email) return;
        setBusy(true);
        try {
            const result = await createClientUser(businessId, { name: name.trim(), email: email.trim() });
            if (!result.ok) { setRefused(result.message); return; }
            snackbar.showSuccess(`Login sent to ${callName(name)}`);
            onClose();
        } catch {
            setRefused("The login was not sent. Check your connection and try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Drawer
            open={open}
            onClose={busy ? stay : onClose}
            title="Add a login"
            sub={`${businessName}. For another person at the client.`}
            width={560}
            foot={
                <>
                    {refused && <Refusal className="basis-full">{refused}</Refusal>}
                    <PillButton variant="primary" className="max-sm:h-9" busy={busy} writes onClick={() => void send()}>Send the login</PillButton>
                    <PillButton className="max-sm:h-9" onClick={onClose}>Not now</PillButton>
                </>
            }
        >
            <div className={cn(SURFACE.block, "space-y-4")}>
                <Field label="Name" error={errors.name}>
                    {({ id, describedBy }) => (
                        <input id={id} aria-describedby={describedBy} aria-invalid={!!errors.name} className={inputClass(errors.name)} value={name} onChange={(e) => { setName(e.target.value); setErrors((was) => ({ ...was, name: undefined })); }} autoComplete="off" />
                    )}
                </Field>
                <Field label="Login email" error={errors.email}>
                    {({ id, describedBy }) => (
                        <input id={id} type="email" aria-describedby={describedBy} aria-invalid={!!errors.email} className={inputClass(errors.email)} value={email} onChange={(e) => { setEmail(e.target.value); setErrors((was) => ({ ...was, email: undefined })); }} autoComplete="off" />
                    )}
                </Field>
            </div>
            {/* Says only what every client with a plan has: an Online one may have no AI check yet. */}
            <DrawerBlock label="What they get">Your Oura login is ready. Set your password to see your plan. The link lasts 7 days.</DrawerBlock>
        </Drawer>
    );
}

/* ------------------------------------------------------------------------- *
 * Correcting the client's contact (#3992)
 * ------------------------------------------------------------------------- */

/**
 * The contact was set once, by New client, and nothing could change it: in the
 * staff test a mistyped email sent the kick-off to an address the client does
 * not use, and the only way forward was to delete the client and create it
 * again.
 *
 * Either field, or both — the server keeps what is left out. Its refusals name
 * the field they are about, so each lands beside the input that caused it.
 */
export function EditContactDrawer({
    open,
    onboarding,
    onSaved,
    onClose,
}: {
    open: boolean;
    onboarding: Onboarding;
    onSaved: (next: Onboarding) => void;
    onClose: () => void;
}) {
    const snackbar = useSnackbar();
    const [name, setName] = useState(onboarding.contact.name);
    const [email, setEmail] = useState(onboarding.contact.email);
    const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
    const [refused, setRefused] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const nameChanged = trimmedName !== onboarding.contact.name.trim();
    const emailChanged = trimmedEmail !== onboarding.contact.email.trim();

    const save = async () => {
        setRefused(null);
        // What the server would refuse anyway, said without a round trip.
        const next: { name?: string; email?: string } = {};
        if (!trimmedName) next.name = "Add a name.";
        if (!trimmedEmail) next.email = "Add an email.";
        else if (!isEmail(trimmedEmail)) next.email = "Check this address.";
        setErrors(next);
        if (Object.keys(next).length > 0) return;
        // Only what moved: a field left out keeps its value, so an unchanged
        // name is never re-sent and never re-logged as a change.
        if (!nameChanged && !emailChanged) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            const r = await updateOnboardingContact(onboarding.businessId, {
                ...(nameChanged ? { name: trimmedName } : {}),
                ...(emailChanged ? { email: trimmedEmail } : {}),
            });
            if (!r.ok) {
                // The server names the field; only a refusal about neither goes to the foot.
                if (r.field) setErrors({ [r.field]: r.message });
                else setRefused(r.message);
                return;
            }
            snackbar.showSuccess("Contact updated", emailChanged ? `Send the ${isOneSetup(onboarding) ? "Setup" : "kick-off"} email again so it reaches the new address.` : undefined);
            onSaved(r.onboarding);
            onClose();
        } catch {
            setRefused("The contact did not change. Check your connection and try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            open={open}
            onClose={saving ? stay : onClose}
            title="The client's contact"
            sub={`${nameStop(onboarding.businessName)} Who Oura writes to.`}
            width={520}
            foot={
                <>
                    {refused && <Refusal className="basis-full">{refused}</Refusal>}
                    <PillButton variant="primary" className="max-sm:h-9" busy={saving} writes onClick={() => void save()}>Save the contact</PillButton>
                    <PillButton className="max-sm:h-9" onClick={onClose}>Cancel</PillButton>
                </>
            }
        >
            <div className={cn(SURFACE.block, "space-y-4")}>
                <Field label="Name" error={errors.name}>
                    {({ id, describedBy }) => (
                        <input
                            id={id}
                            aria-describedby={describedBy}
                            value={name}
                            maxLength={120}
                            autoComplete="off"
                            disabled={saving}
                            onChange={(e) => { setName(e.target.value); setErrors((was) => ({ ...was, name: undefined })); setRefused(null); }}
                            className={inputClass(errors.name)}
                        />
                    )}
                </Field>
                <Field label="Email" hint="Everything Oura sends this client goes here, including their login." error={errors.email}>
                    {({ id, describedBy }) => (
                        <input
                            id={id}
                            type="email"
                            inputMode="email"
                            aria-describedby={describedBy}
                            value={email}
                            maxLength={255}
                            autoComplete="off"
                            spellCheck={false}
                            disabled={saving}
                            onChange={(e) => { setEmail(e.target.value); setErrors((was) => ({ ...was, email: undefined })); setRefused(null); }}
                            className={inputClass(errors.email)}
                        />
                    )}
                </Field>
            </div>
            {emailChanged && !errors.email && (
                <DrawerBlock label="After this">
                    Nothing already sent changes. Send the kick-off email again from the Emails card so it reaches {trimmedEmail}.
                </DrawerBlock>
            )}
        </Drawer>
    );
}
